/**
 * Per-lane symmetric deleting ratchet for the Fume session container.
 *
 *   messageKey_i  = HKDF(chainKey_i, info = INFO_MESSAGE)
 *   chainKey_{i+1} = HKDF(chainKey_i, info = INFO_CHAIN)
 *
 * Advancing zero-fills the spent chain key, so a chain-key buffer captured
 * before an advance reads all-zero afterwards. That is the per-message
 * forward-secrecy property: once a lane has moved past index i, index i's key
 * material is unrecoverable — a device grabbed mid-conversation cannot recover
 * already-sent capsules. No invented primitive: HKDF-SHA256 from @fume/core.
 */
import { hkdfSha256 } from '@fume/core';
const utf8 = new TextEncoder();
const KEY_SIZE = 32;
const INFO_MESSAGE = utf8.encode('fume/session/ratchet/msg/v1');
const INFO_CHAIN = utf8.encode('fume/session/ratchet/chain/v1');
export class Ratchet {
    chainKey;
    nextSeq;
    destroyed = false;
    constructor(laneRoot, startSeq = 0) {
        if (!(laneRoot instanceof Uint8Array) || laneRoot.length !== KEY_SIZE) {
            throw new RangeError('laneRoot must be exactly 32 bytes');
        }
        if (!Number.isInteger(startSeq) || startSeq < 0) {
            throw new RangeError('startSeq must be a non-negative integer');
        }
        // Copy so the caller's buffer and ours have independent lifetimes.
        this.chainKey = laneRoot.slice();
        this.nextSeq = startSeq;
    }
    /** Sequence index the next {@link next} call will emit. */
    get seq() {
        return this.nextSeq;
    }
    get isDestroyed() {
        return this.destroyed;
    }
    /**
     * Emit the next message key and advance the chain, zero-filling the spent
     * chain key. Returns the key and the sequence index it belongs to.
     */
    next() {
        if (this.destroyed) {
            throw new Error('ratchet is destroyed');
        }
        const key = hkdfSha256(this.chainKey, null, INFO_MESSAGE, KEY_SIZE);
        const nextChain = hkdfSha256(this.chainKey, null, INFO_CHAIN, KEY_SIZE);
        this.chainKey.fill(0); // delete the spent chain key (forward secrecy)
        this.chainKey = nextChain;
        const seq = this.nextSeq;
        this.nextSeq += 1;
        return { key, seq };
    }
    /**
     * Advance until positioned to emit `targetSeq`, discarding (zero-filling) the
     * skipped message keys. Used by a receiver skipping forward over unseen
     * capsules. Bounded by `maxSkip` to bound work and the replay surface.
     */
    advanceTo(targetSeq, maxSkip = 1024) {
        if (this.destroyed) {
            throw new Error('ratchet is destroyed');
        }
        if (!Number.isInteger(targetSeq) || targetSeq < 0) {
            throw new RangeError('targetSeq must be a non-negative integer');
        }
        if (targetSeq < this.nextSeq) {
            throw new Error(`cannot rewind ratchet: targetSeq ${targetSeq} < current ${this.nextSeq} (past keys erased)`);
        }
        if (targetSeq - this.nextSeq > maxSkip) {
            throw new Error(`skip-ahead of ${targetSeq - this.nextSeq} exceeds maxSkip ${maxSkip}`);
        }
        while (this.nextSeq < targetSeq) {
            const step = this.next();
            step.key.fill(0); // discard skipped message key
        }
    }
    /** Zero-fill the live chain key. Idempotent. */
    destroy() {
        if (this.destroyed)
            return;
        this.chainKey.fill(0);
        this.destroyed = true;
    }
    /** TEST HOOK ONLY: the live chain-key buffer reference (to observe erasure). */
    chainKeyRefForTest() {
        return this.chainKey;
    }
}
//# sourceMappingURL=ratchet.js.map